import { Alert } from '@ksyos/design-system';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { imagePdfAndDocExtensions } from '@common/utils/fileExtensions';
import { CancelButton } from '@frontend/components/elements/carePathButtons/CancelButton';
import type { ConclusieDiagnoseOption } from '../../../../../../common/enums/conclusieDiagnoseOption';
import type { ICallBackMomentAddEdit, ITimeRange } from '../../../../../../common/validation/callBackMoment';
import type { ICareProviderContactDraft } from '../../../../../../common/validation/careProviderContact';
import type { ITimeSlots } from '../../../../../../common/validation/timeSlots';
import type { IReduxState } from '../../../../../configureStore';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { IMedication } from '../../../../interfaces/medication';
import type { ICustomControlRequestAddEditProps } from '../../interfaces/';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { DayOfWeek } from '../../../../../../common/enums/dayOfWeek';
import { FilesUrlBase } from '../../../../../../common/enums/filesUrlBase';
import { PractitionerRoleInRequest } from '../../../../../../common/enums/practitionerRoleInRequest';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../common/enums/toastType';
import { validateTimePeriod, validateTimePeriodOverlap } from '../../../../../../common/validation/timeSlots';
import { getJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { useViewportWidth } from '../../../../../utils/hooks/useViewportWidth';
import { showServiceToast } from '../../../../../utils/showToast';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { CareProviderContactAddEdit } from '../../../../elements/CareProviderContactAddEdit';
import { ButtonContainer, Form, FormDivider, HeaderDivider, InputTextArea } from '../../../../elements/common';
import { DiagnosisQuestionsAddEdit } from '../../../../elements/DiagnosisQuestionsAddEdit';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { FormSection } from '../../../../elements/forms/formSection/FormSection';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { CallBackMomentAddEdit } from '../../../../elements/tables/callBackMoment/CallBackMomentAddEdit';
import { ReloadEpisode } from '../../../episodes/reloadEpisode';
import { ProfessionalSummaryData } from '../../../professionalSummaryData/ProfessionalSummaryData';
import { addFormAnalytics, addFormConversion, addTrackFormSubmit } from '../../utils/formAnalytics';
import { AssetDropzoneWithActions } from '../../utils/v1/AssetDropzoneWithActions';
import { CareProviderSelector } from '../../utils/v1/CareProviderSelector';
import { MedicationsAddEdit } from '../../utils/v1/medications/MedicationsAddEdit';
import { NewOrEditRequestProcessContainer } from '../../utils/v1/NewOrEditRequestProcessContainer';
import { ServiceConsumerSupervisorSelector } from '../../utils/v1/ServiceConsumerSupervisorSelector';
import { ServiceOrganizationProvider } from '../../utils/v1/ServiceOrganizationProvider';
import { Ggzct1FollowingSteps } from './GgzctV1FollowingSteps';

interface ICustomControlDataGgzct {
    beschikbaarheid: ICallBackMomentAddEdit[];
    conclusie_diagnose: ConclusieDiagnoseOption | null;
    contactgegevens_specialist: ICareProviderContactDraft;
    remarks: string | null;
    toelichting: string | null;
    vraag: string | null;
}

export function GgzctV1RequestAddEdit(props: ICustomControlRequestAddEditProps) {
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const emailAddress = useSelector((state: IReduxState) => state.authentication.emailAddress);

    const formRef = useRef<HTMLFormElement>(null);
    const saveSendButtonRef = useRef<HTMLButtonElement>(null);

    const { width } = useViewportWidth();

    useEffect(() => {
        if (!props.request.id) {
            loadContactDetails();
        }

        initializeNewCustomControlDataFields();

        addFormAnalytics(formRef.current, saveSendButtonRef.current);
    }, []);

    return (
        <NewOrEditRequestProcessContainer
            isFirstItem={props.isFirstItem}
            itemState={props.itemState}
            process={props.process}
            service={props.service}
            title={getServiceLabel(props.service.code, 'request', props.service.name)}
        >
            <Form
                formRef={formRef}
                id="GgzctV1RequestAddEditId"
                name="GgzctV1RequestAddEdit"
                onSubmit={async () => null}
            >
                <CareProviderSelector
                    {...props}
                    entity={props.request}
                />

                <ServiceOrganizationProvider
                    errors={props.errors}
                    initialOrganizationId={props.request.selectedDiagnosticReportProviderOrganizationId}
                    initialPractitionerId={props.request.selectedDiagnosticReportProviderPractitionerId}
                    onChange={async (organizationId, practitionerId) => {
                        await props.handleChange('selectedDiagnosticReportProviderOrganizationId', organizationId);
                        await props.handleChange('selectedDiagnosticReportProviderPractitionerId', practitionerId);
                    }}
                    onDoValidate={async () => await props.validateOne('selectedDiagnosticReportProviderOrganizationId')}
                    roleOfPractitionersInList={PractitionerRoleInRequest.specialist}
                    searchInNetwork
                    serviceProviderServiceId={props.service.id}
                    serviceType={props.service.serviceType}
                />

                <ServiceConsumerSupervisorSelector {...props} />

                <FormDivider />

                <HeaderDivider text={translateKey('procedure.procedure')} />

                <AssetDropzoneWithActions
                    acceptedFiles={imagePdfAndDocExtensions}
                    assets={props.request.assets}
                    assetsBaseUrl={FilesUrlBase.requests}
                    cleanDropzoneAfterCompletingFiles={props.isAutoSaveActive}
                    onChange={async (assets) => await props.handleChange('assets', assets)}
                    onUploading={async (isUploading) => setButtonDisabled(isUploading)}
                    processActivityId={props.request.id}
                    service={props.service}
                    showAdditionalFilesCheckbox={props.previousRequest !== undefined}
                    updateNewRequestPreviousRequest={props.updateNewRequestPreviousRequest}
                />

                <FormDivider />

                <MedicationsAddEdit
                    entity={ProcessEntity.request}
                    entityId={props.request.id}
                    errors={props.errors}
                    handleChangePatientUsesMedication={async (patientUsesMedication: boolean) =>
                        await props.handleChange('patientUsesMedication', patientUsesMedication)
                    }
                    onChange={async (medications: IMedication[]) =>
                        await props.handleChange('requestMedications', medications)
                    }
                    patientUsesMedication={props.request.patientUsesMedication}
                    validateMedications={async () => await props.validateOne('requestMedications')}
                    validatePatientUsesMedication={async () => await props.validateOne('patientUsesMedication')}
                />

                <ProfessionalSummaryData
                    entity={ProcessEntity.request}
                    entityId={props.request.id}
                    errors={props.errors}
                    handleChangeAllergies={async (allergies) => await props.handleChange('allergies', allergies)}
                    handleChangeEpisodes={async (episodesAndJournal) =>
                        await props.handleChange('requestEpisodesAndJournal', episodesAndJournal)
                    }
                    handleChangeLabResults={async (labResults) =>
                        await props.handleChange('requestLabResults', labResults)
                    }
                    validateEpisodesAndJournals={async () => await props.validateOne('requestEpisodesAndJournal')}
                />

                <DiagnosisQuestionsAddEdit {...props} />

                <FormSection title="Terugbelmoment">
                    <Alert appearance="info">
                        Geef aan op welke tijden je de aankomende dagen bereikbaar bent voor de specialist
                    </Alert>
                    <HorizontalFormGroup>
                        {width < 1200 ? (
                            <FormLabel
                                className="col-sm-24"
                                isRequired
                            >
                                Dagen en tijden
                            </FormLabel>
                        ) : (
                            <FormLabel isRequired>Dagen en tijden</FormLabel>
                        )}
                        <InputContainer>
                            <CallBackMomentAddEdit
                                callBackMoments={props.request.customControlData.beschikbaarheid}
                                errors={props.errors}
                                handleChange={async (callBackMoments) =>
                                    await props.handleChangeCustomControl({ beschikbaarheid: callBackMoments })
                                }
                                onDoValidate={async (key: string) => await props.validateOneCustomControl(key)}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={props.errors.has('customControlData.remarks')}>
                        <FormLabel>{translateKey('common.remarks')}</FormLabel>
                        <InputContainer>
                            <InputTextArea
                                error={props.errors.get('customControlData.remarks')}
                                onChange={async (value) => await props.handleChangeCustomControl({ remarks: value })}
                                onDoValidate={async () => await props.validateOneCustomControl('remarks')}
                                placeholder={'Licht hier je telefonische bereikbaarheid toe...'}
                                value={props.request.customControlData.remarks}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <CareProviderContactAddEdit {...props} />
                </FormSection>

                <Ggzct1FollowingSteps />
                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={props.onCancel}
                            entity={ProcessEntity.request}
                            isExisting={!!props.request.id}
                            revertChanges={props.revertRequestChanges}
                            serviceCode={props.service.code}
                            serviceType={props.service.serviceType}
                        />,
                        <SaveButton
                            buttonDisabled={buttonDisabled}
                            save={async () => {
                                await saveRequest('draft');
                            }}
                        />,
                        <SaveSendButton
                            buttonDisabled={buttonDisabled}
                            saveSend={async () => {
                                await saveRequest('final');
                            }}
                            saveSendButtonRef={saveSendButtonRef}
                        />,
                    ]}
                />
            </Form>
        </NewOrEditRequestProcessContainer>
    );

    async function loadContactDetails() {
        const result = await getJson(
            `/api/serviceConsumers?serviceId=${props.service.id}&employmentId=${props.employmentId}&isActive=true`
        );
        await props.handleChangeCustomControl({
            contactgegevens_specialist: {
                phoneNumber: result.json.items[0]?.phoneNumberConsult,
                emailAddress,
            },
        });
    }

    async function initializeNewCustomControlDataFields() {
        if (props.request.customControlData) {
            if (props.request.customControlData.remarks === undefined) {
                await props.handleChangeCustomControl({ remarks: null });
            }
        }
    }

    async function saveRequest(saveType: 'draft' | 'final') {
        setButtonDisabled(true);

        addTrackFormSubmit(saveSendButtonRef.current);

        const validatedRequest = await props.validateRequest(saveType);
        if (!validatedRequest) {
            setButtonDisabled(false);

            return;
        }

        const requestId = await props.onSaveRequest(saveType);

        if (saveType === 'final') {
            const result = await getJson(
                `/api/organizations/${props.request.selectedDiagnosticReportProviderOrganizationId}`
            );
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                props.service,
                'request',
                translateKey('toast.consultRequestSubmit', result.json.name)
            );
            addFormConversion(formRef.current);
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
        }

        await props.onSave(requestId);
        if (props.reloadProcess) {
            await props.reloadProcess(ReloadEpisode.reloadAll);
        }
    }
}

export function ggzctV1InitializeCustomControlData(): ICustomControlDataGgzct {
    return {
        conclusie_diagnose: null,
        vraag: null,
        toelichting: null,
        contactgegevens_specialist: {
            phoneNumber: null,
            emailAddress: null,
        },
        beschikbaarheid: Object.values(DayOfWeek).map((day: DayOfWeek) => ({
            dayOfWeek: day,
            isChecked: false,
            periodOne: {
                startTime: null,
                endTime: null,
            },
            periodTwo: {
                startTime: null,
                endTime: null,
            },
        })),
        remarks: null,
    };
}

export function ggzctV1RequestValidateOneCustomControlData(
    name: string,
    customControlData: ICustomControlDataGgzct,
    errors: ValidationErrors
) {
    let validationErrors = errors;

    // Parameter name layout in case of startTime: beschikbaarheid.0.periodOne.startTime
    const nameKeys = name.split('.');
    if (nameKeys.length === 4) {
        const [, indexKey, periodKey] = nameKeys;
        assertIsValidPeriodName(periodKey);

        const index = parseInt(indexKey, 10);
        const periodFieldKey = `customControlData.beschikbaarheid.${index}.${periodKey}`;
        validationErrors = errors.remove(periodFieldKey);

        const beschikbaarheid = customControlData.beschikbaarheid[index];
        if (beschikbaarheid && beschikbaarheid.isChecked) {
            const period = beschikbaarheid[periodKey] as ITimeRange;
            validationErrors = validateTimePeriod(period, validationErrors, periodFieldKey);
        }
    }

    return validationErrors;
}

export function ggzctV1RequestValidateCustomControlData(
    customControlData: ICustomControlDataGgzct,
    errors: ValidationErrors
) {
    let validationErrors = errors;

    for (let index = 0; index < customControlData.beschikbaarheid.length; index++) {
        const beschikbaarheid = customControlData.beschikbaarheid[index];
        const periodOneFieldKey = `customControlData.beschikbaarheid.${index}.periodOne`;
        const periodTwoFieldKey = `customControlData.beschikbaarheid.${index}.periodTwo`;

        validationErrors = validationErrors.remove(periodOneFieldKey);
        validationErrors = validationErrors.remove(periodTwoFieldKey);

        if (beschikbaarheid.isChecked) {
            validationErrors = validateTimePeriod(beschikbaarheid.periodOne, validationErrors, periodOneFieldKey);
            validationErrors = validateTimePeriod(beschikbaarheid.periodTwo, validationErrors, periodTwoFieldKey);

            if (!validationErrors.has(periodTwoFieldKey)) {
                validationErrors = validateTimePeriodOverlap(
                    {
                        firstTimeRange: beschikbaarheid.periodOne,
                        secondTimeRange: beschikbaarheid.periodTwo,
                    } as ITimeSlots,
                    validationErrors,
                    periodTwoFieldKey
                );
            }
        }
    }

    return validationErrors;
}

function assertIsValidPeriodName(periodName: string): asserts periodName is 'periodOne' | 'periodTwo' {
    if (periodName !== 'periodOne' && periodName !== 'periodTwo') {
        throw new Error('Not a valid period name');
    }
}
